<template>
  <div>
    <Nav></Nav>
    <b-overlay :show="loadingReports" rounded="sm" spinner-type="grow">
      <b-container fluid>
        <b-row>
          <b-col>
            <h1>Reporte de Consumo de Recetas</h1>
            <p class="text-muted">
              Análisis de consumo de insumos basado en productos vendidos
            </p>
          </b-col>
        </b-row>

        <!-- Search/Filter Bar -->
        <ReportSearchBar @search="searchRecv" :emitOnStart="true">
        </ReportSearchBar>

        <!-- Export Buttons -->
        <b-row v-if="reportTable.length > 0" class="mt-3">
          <b-col>
            <b-button variant="primary" @click="exportToExcel" class="mr-2">
              <b-icon icon="file-earmark-excel"></b-icon>
              Descargar Excel
            </b-button>
            <download-csv
              :data="reportTable"
              class="btn btn-success"
              :name="
                'consumoRecetas_' +
                dates.initial_date +
                '_to_' +
                dates.end_date +
                '.csv'
              "
              separator-excel
            >
              <b-icon icon="file-earmark-text"></b-icon>
              Descargar CSV
            </download-csv>
          </b-col>
        </b-row>

        <!-- Summary Statistics -->
        <b-row v-if="reportTable.length > 0" class="mt-3">
          <b-col md="3">
            <b-card bg-variant="info" text-variant="white">
              <h6>Total Productos</h6>
              <h3>{{ uniqueProducts }}</h3>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card bg-variant="success" text-variant="white">
              <h6>Total Insumos</h6>
              <h3>{{ uniqueSupplies }}</h3>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card bg-variant="warning" text-variant="white">
              <h6>Unidades Vendidas</h6>
              <h3>{{ totalSold }}</h3>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card bg-variant="primary" text-variant="white">
              <h6>Total Registros</h6>
              <h3>{{ reportTable.length }}</h3>
            </b-card>
          </b-col>
        </b-row>

        <!-- Data Table -->
        <b-card class="mt-4" v-if="reportTable.length > 0">
          <b-row>
            <b-col md="6">
              <b-form-group label="Buscar:">
                <b-form-input
                  type="search"
                  v-model="search"
                  @keyup="page = 1"
                  debounce="500"
                  placeholder="Buscar por producto o insumo..."
                >
                </b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6" class="text-right">
              <b-form-group label="Registros por página:">
                <b-form-select v-model="byPage" :options="perPageOptions">
                </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-table
            small
            striped
            hover
            responsive
            :items="reportTable"
            :fields="tableFields"
            :per-page="byPage"
            :current-page="page"
            :filter="search"
          >
            <!-- Custom formatting for numeric columns -->
            <template #cell(avg_base_portion)="data">
              {{ data.value.toFixed(4) }}
            </template>
            <template #cell(total_consumed)="data">
              {{ data.value.toFixed(4) }}
            </template>
          </b-table>

          <b-pagination
            v-model="page"
            :total-rows="reportTable.length"
            :per-page="byPage"
            align="center"
            size="sm"
          >
          </b-pagination>
        </b-card>

        <!-- Empty State -->
        <b-card v-if="!loadingReports && reportTable.length === 0" class="mt-4">
          <div class="text-center py-5">
            <b-icon icon="inbox" font-scale="3" variant="secondary"></b-icon>
            <h4 class="mt-3">No hay datos disponibles</h4>
            <p class="text-muted">
              Selecciona un rango de fechas y sucursales para generar el
              reporte.
            </p>
          </div>
        </b-card>
      </b-container>
    </b-overlay>
  </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import ReportSearchBar from "./searchBar/ReportSearchBar.vue";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";

export default {
  name: "RecipeConsumptionReport",
  components: { Nav, ReportSearchBar },
  data() {
    return {
      loadingReports: false,
      reportTable: [],
      search: "",
      byPage: 30,
      page: 1,
      perPageOptions: [10, 20, 30, 50, 100],
      sucOptions: [],
      dates: {
        initial_date: "",
        end_date: "",
        sucursales: [],
      },
      tableFields: [
        {
          key: "product_id",
          label: "ID Producto",
          sortable: true,
          class: "text-center",
        },
        {
          key: "product_name",
          label: "Producto",
          sortable: true,
          class: "text-left",
        },
        {
          key: "quantity_sold",
          label: "Vendido",
          sortable: true,
          class: "text-center",
        },
        {
          key: "supply_code",
          label: "Código Insumo",
          sortable: true,
          class: "text-center",
        },
        {
          key: "supply_name",
          label: "Insumo",
          sortable: true,
          class: "text-left",
        },
        {
          key: "avg_base_portion",
          label: "Porción Base (Prom.)",
          sortable: true,
          class: "text-right",
        },
        {
          key: "total_consumed",
          label: "Total Consumido",
          sortable: true,
          class: "text-right",
        },
        {
          key: "supply_unit",
          label: "Unidad",
          sortable: true,
          class: "text-center",
        },
      ],
    };
  },
  computed: {
    uniqueProducts() {
      const unique = new Set(this.reportTable.map((item) => item.product_id));
      return unique.size;
    },
    uniqueSupplies() {
      const unique = new Set(this.reportTable.map((item) => item.supply_id));
      return unique.size;
    },
    totalSold() {
      return this.reportTable.reduce(
        (sum, item) => sum + item.quantity_sold,
        0
      );
    },
  },
  mounted() {
    this.getSucursals();
  },
  methods: {
    searchRecv(p) {
      this.dates.end_date = moment(p.endDate).format("YYYY-MM-DD");
      this.dates.initial_date = moment(p.initDate).format("YYYY-MM-DD");
      this.dates.sucursales = p.sucursales;
      this.getReport();
    },
    getReport() {
      this.loadingReports = true;
      axios
        .post("/report/recipe-consumption", this.dates)
        .then((response) => {
          this.reportTable = response.data || [];
          this.page = 1;
          if (this.reportTable.length === 0) {
            this.makeToast(
              "Información",
              "No se encontraron datos para el rango seleccionado",
              "warning"
            );
          }
        })
        .catch((err) => {
          console.error(err);
          this.reportTable = [];
          this.makeToast(
            "Error",
            err.response?.data || "Error al obtener el reporte",
            "danger"
          );
        })
        .finally(() => {
          this.loadingReports = false;
        });
    },
    exportToExcel() {
      const workbook = XLSX.utils.book_new();
      const headers = this.tableFields.map((field) => field.label);
      const data = [headers];

      this.reportTable.forEach((item) => {
        const row = [];
        this.tableFields.forEach((field) => {
          let value = item[field.key] || "";
          // Format numbers for Excel
          if (
            field.key === "avg_base_portion" ||
            field.key === "total_consumed"
          ) {
            value = parseFloat(value).toFixed(4);
          }
          row.push(value);
        });
        data.push(row);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(data);

      // Auto-size columns
      const maxWidth = 50;
      const colWidths = [];
      for (let i = 0; i < headers.length; i++) {
        let maxLen = headers[i].length;
        data.forEach((row) => {
          if (row[i]) {
            const len = row[i].toString().length;
            if (len > maxLen) maxLen = len;
          }
        });
        colWidths.push({ wch: Math.min(maxLen + 2, maxWidth) });
      }
      worksheet["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Consumo de Recetas");
      XLSX.writeFile(
        workbook,
        `consumoRecetas_${this.dates.initial_date}_to_${this.dates.end_date}.xlsx`
      );

      this.makeToast(
        "Éxito",
        "Archivo Excel descargado correctamente",
        "success"
      );
    },
    getSucursals() {
      axios
        .get("/sucursals")
        .then((response) => {
          this.sucOptions = [];
          this.dates.sucursales = [];
          response.data.forEach((element) => {
            this.sucOptions.push({ text: element.nombre, value: element.id });
            this.dates.sucursales.push(element.id);
          });
        })
        .catch((err) => {
          console.error(err);
          this.makeToast(
            "Error",
            err.response?.data || "Error al cargar las sucursales",
            "danger"
          );
        });
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, {
        title,
        variant,
        autoHideDelay: 5000,
        appendToast: true,
        solid: true,
      });
    },
  },
};
</script>

<style scoped>
.b-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

h1 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.text-muted {
  font-size: 0.95rem;
}
</style>